import type { Survey, SurveyExport } from '@citizen-walk/shared-types';

/**
 * アンケート単位 JSON export / import helper (AW8、docs/108 High)。
 *
 * 旧2 `old2/.../apps/admin/src/lib/surveys.ts::buildSurveyExport / parseSurveyExport` を踏襲。
 * client-side で完結する pure 関数として切出 (callable 不要、admin が browser から直接 Blob 出力 /
 * file 取込)。
 *
 * 踏襲した点:
 *   - exportType: 'survey' / version: 1 の envelope schema
 *   - 監査・所有・マスター連携メタ・集計キャッシュを export から除外 (別環境で無効なため)
 *   - parse 時に exportType === 'survey' && questions が配列 であることを検証
 * 変えた点:
 *   - downloadJson / fileInput は呼び出し側 (page) に委譲し、本 module は純変換のみ
 */

/** Survey から export 用 envelope を生成 (監査 / 連携メタ / 集計を strip)。 */
export function buildSurveyExport(survey: Survey, sourceDistrictName?: string): SurveyExport {
  const {
    id: _id,
    createdAt: _ca,
    updatedAt: _ua,
    createdBy: _cb,
    updatedBy: _ub,
    responseCount: _rc,
    version: _v,
    sourceMasterSurveyId: _sm,
    linkedToMaster: _lm,
    masterVersion: _mv,
    ...rest
  } = survey;
  void _id;
  void _ca;
  void _ua;
  void _cb;
  void _ub;
  void _rc;
  void _v;
  void _sm;
  void _lm;
  void _mv;
  return {
    exportType: 'survey',
    exportedAt: new Date().toISOString(),
    version: 1,
    ...(sourceDistrictName ? { sourceDistrictName } : {}),
    survey: rest,
  };
}

/** export JSON 文字列を parse + 妥当性検証。失敗時は日本語メッセージで throw。 */
export function parseSurveyExport(text: string): SurveyExport {
  let parsed: Partial<SurveyExport>;
  try {
    parsed = JSON.parse(text) as Partial<SurveyExport>;
  } catch {
    throw new Error('JSON の解析に失敗しました');
  }
  if (parsed.exportType !== 'survey' || !parsed.survey) {
    throw new Error('アンケートの設定ファイルではありません');
  }
  if (!Array.isArray(parsed.survey.questions)) {
    throw new Error('questions が配列ではありません');
  }
  return parsed as SurveyExport;
}

/** export 用ファイル名 `<title|id>_yyyy-mm-dd.json` (旧2 と同 sanitize)。 */
export function buildSurveyExportFilename(survey: Survey, now: Date = new Date()): string {
  const base = (survey.title || survey.id).replace(/[^\w\-一-龯ぁ-ゔァ-ヴー]/g, '_');
  return `${base}_${now.toISOString().slice(0, 10)}.json`;
}

/** Blob → ObjectURL → <a download> でブラウザ DL (JSON、BOM 無し)。 */
export function downloadSurveyJson(filename: string, obj: unknown): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
